import { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { aboutFeatures, aboutImage, stats } from "../mock";

const useCountUp = (target, active, duration = 1600) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = null;
    let raf;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  return val;
};

const StatItem = ({ stat, active }) => {
  const count = useCountUp(stat.value, active);
  return (
    <div className="text-center sm:text-left">
      <div className="font-display text-[40px] sm:text-[46px] text-white leading-none" style={{ fontWeight: 800 }}>
        {count}
        <span className="text-blue-400">{stat.suffix}</span>
      </div>
      <div className="mt-2 text-[14px] text-slate-300">{stat.label}</div>
    </div>
  );
};

const About = () => {
  const [active, setActive] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setActive(true),
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="ueber-uns" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Image */}
          <div className="reveal relative order-2 lg:order-1">
            <div className="absolute -top-5 -left-5 w-32 h-32 rounded-3xl bg-blue-600 hidden sm:block" />
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-300/60 ring-1 ring-slate-200/60">
              <img
                src={aboutImage}
                alt="Büroreinigung in Augsburg – Reinigungskraft von MAG Facility Services reinigt einen Arbeitsplatz mit Handschuhen"
                width="900"
                height="600"
                loading="lazy"
                decoding="async"
                className="w-full h-[420px] lg:h-[500px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-4 sm:right-8 bg-white rounded-2xl shadow-xl border border-slate-100 px-5 py-4 flex items-center gap-3">
              <div className="font-display text-[26px] text-slate-900" style={{ fontWeight: 800 }}>
                5,0<span className="text-blue-600">★</span>
              </div>
              <div className="text-[13px] text-slate-500 leading-tight">
                Von unseren Kunden
                <br />
                bestens bewertet
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="order-1 lg:order-2">
            <div className="reveal">
              <span className="text-[13px] font-bold tracking-[0.22em] text-blue-600 uppercase">
                Über uns
              </span>
              <h2 className="font-display mt-3 text-slate-900 text-[30px] sm:text-[44px] tracking-tight leading-[1.05]" style={{ fontWeight: 900 }}>
                Warum MAG Facility Services?
              </h2>
              <p className="mt-4 text-[17px] text-slate-600 leading-relaxed">
                Seit Jahren kümmern wir uns in Augsburg und Umgebung um Objekte,
                in denen Menschen arbeiten, wohnen und Gäste empfangen. Mit einem
                geschulten Team, klaren Standards und höchstem Anspruch an
                Zuverlässigkeit.
              </p>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-6">
              {aboutFeatures.map((f) => {
                const Icon = Icons[f.icon] || Icons.Check;
                return (
                  <div key={f.title} className="reveal flex gap-4">
                    <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-blue-50 text-blue-600 shrink-0">
                      <Icon className="w-5 h-5" strokeWidth={2.2} />
                    </div>
                    <div>
                      <h3 className="text-[16px] font-bold text-slate-900">
                        {f.title}
                      </h3>
                      <p className="mt-1 text-[14px] text-slate-600 leading-relaxed">
                        {f.text}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div
          ref={ref}
          className="reveal mt-16 rounded-3xl bg-slate-900 px-8 py-10 lg:px-14 grid grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {stats.map((s) => (
            <StatItem key={s.label} stat={s} active={active} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
